import {BidBaseGetQuery} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidBaseGetQuery';
import {FilterOperator} from '@app/utils/filter-operator';
import {BID_STATUS} from '@shared/data/bid-status-uses-data';

export const FIELD_PHOTOS_EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** Minimal bid projection for choosing the ZIP linked to field photos. */
export const BID_SELECT_FIELDS_FOR_FIELD_PHOTOS: readonly string[] = [
  'id',
  'fileResultId',
  'bidState',
  'bidStateId',
  'lastUpdateAt',
  'updateDate',
  'endDate',
  'createDate',
];

const ZIP_IMAGE_KEY = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;

export function buildFieldPhotoBidsQuery(fieldId: string): BidBaseGetQuery {
  return {
    take: 500,
    offset: 0,
    selectFields: [...BID_SELECT_FIELDS_FOR_FIELD_PHOTOS],
    filters: [
      {
        filterField: 'fieldId',
        filterOperator: FilterOperator.Equals,
        filterValue: String(fieldId),
      },
    ],
  };
}

/** Numeric workflow state; prefer `bidState`, use `bidStateId` only when it is a finite number (not a UUID). */
export function parseBidWorkflowState(bid: Record<string, unknown>): number | null {
  const rawState = bid['bidState'];
  if (rawState != null && String(rawState).trim() !== '') {
    const n = Number(rawState);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  const rawId = bid['bidStateId'];
  if (rawId != null && String(rawId).trim() !== '') {
    const n = Number(rawId);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

export function bidSortTimeMs(bid: Record<string, unknown>): number {
  const raw =
    bid['lastUpdateAt'] ??
    bid['LastUpdateAt'] ??
    bid['updateDate'] ??
    bid['endDate'] ??
    bid['createDate'] ??
    bid['CreateDate'] ??
    null;
  if (raw == null || raw === '') {
    return 0;
  }
  const t = new Date(raw as string).getTime();
  return Number.isFinite(t) ? t : 0;
}

function isValidFileResultId(value: unknown, emptyGuid: string): boolean {
  if (value == null) {
    return false;
  }
  const s = String(value).trim();
  return s !== '' && s !== emptyGuid;
}

function bidEligibleForFieldPhotoZip(bid: Record<string, unknown>, emptyGuid: string): boolean {
  if (!isValidFileResultId(bid['fileResultId'], emptyGuid)) {
    return false;
  }
  const s = parseBidWorkflowState(bid);
  return s === BID_STATUS.Finished || s === BID_STATUS.Archive;
}

/**
 * From bid list API payload, returns `fileResultId` of the most recently updated finished/archived bid with a file.
 */
export function pickLatestFieldPhotoBidFileResultId(bids: unknown[], emptyGuid: string): string | null {
  const list = Array.isArray(bids) ? bids : [];
  const candidates: Record<string, unknown>[] = [];
  for (const raw of list) {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const b = raw as Record<string, unknown>;
      if (bidEligibleForFieldPhotoZip(b, emptyGuid)) {
        candidates.push(b);
      }
    }
  }
  if (!candidates.length) {
    return null;
  }
  candidates.sort((a, b) => bidSortTimeMs(b) - bidSortTimeMs(a));
  const id = candidates[0]?.['fileResultId'];
  return id != null ? String(id) : null;
}

export function extractZipImageGalleryItems(map: Map<string, string | Blob>): { src: string; name: string }[] {
  const images: { src: string; name: string }[] = [];
  map.forEach((val, key) => {
    if (typeof val === 'string' && val.startsWith('blob:') && ZIP_IMAGE_KEY.test(key)) {
      images.push({ src: val, name: key });
    }
  });
  return images;
}

/** Progress 0–85 from HTTP download ratio (ZIP unpack uses 85–100 separately). */
export function fieldPhotosHttpProgressPercent(loaded: number, total: number): number {
  if (!total) {
    return 0;
  }
  return Math.min(85, Math.round((loaded / total) * 85));
}

/** Progress 85–100 from JSZip extraction callback (0–100). */
export function fieldPhotosZipExtractOverlayPercent(zipProgress0to100: number): number {
  return 85 + Math.round((Math.min(100, zipProgress0to100) / 100) * 15);
}

export function resolveFieldDetailsDataUpdatedDisplay(params: {
  lastBidDateRaw: unknown;
  selectedTimelineDate: Date | null;
  loadedAt: Date | null;
}): Date | null {
  const { lastBidDateRaw, selectedTimelineDate, loadedAt } = params;
  if (lastBidDateRaw != null && String(lastBidDateRaw).trim() !== '') {
    const d = new Date(lastBidDateRaw as string);
    if (Number.isFinite(d.getTime())) {
      return d;
    }
  }
  if (selectedTimelineDate) {
    return selectedTimelineDate;
  }
  return loadedAt;
}

export function clampPhotoSliderIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }
  return Math.min(Math.max(0, index), length - 1);
}
